/**
 * @description
 * Block Plot Options Generator (Long script but Highcharts configuration is verbose ;) ).
 *
 * - @see definePoints: define serie's points from a sub dataset.
 * - @see defineSeries: define series from a block and a dataset.
 * - @see defineSeriesXAxis: define xAxis from series.
 * - @see defineSeriesYAxis: define yAxis from series.
 * - @see defineOptions: define Highcharts plot options from a block and a dataset.
 *
 * @note
 * We use specific formatter for points and axis labels.
 * - @see pointValueFormatter: format point value.
 * - @see axisLabelFormatter: format axis label.
 */
import { css } from "@linaria/core";
import type {
  AlignValue,
  NavigatorXAxisBreaksOptions,
  NavigatorXAxisPlotBandsOptions,
} from "highcharts";
import memoize from "lodash.memoize";
import { DateTime } from "luxon";
import React, { Fragment } from "react";
import { renderToString } from "react-dom/server";
import t from "../../../../../../../../../i18n";
import { locale } from "../../../../../../../../../i18n/locale";
import { uniqBy } from "diagnostics-lib/array";
import { groupBy } from "diagnostics-lib/iterable";
import { omit as omitObject, pick as pickObject } from "diagnostics-lib/object";
import type { Options } from "@ui/Highcharts";
import Text from "@ui/Text";
import { colors, spacings } from "@ui/Theme";
import type { BlockPlotModelT } from "../../../../../../schema/block/plot";
import { convertToAxisValue } from "./convertToAxisValue";
import { DataType } from "./dataType";
import {
  enableValidityMarker,
  getDefaultColor,
  getDefaultSequentialColor,
  getValidityColor,
  getValidityShape,
} from "./mark";
import {
  createAxisOvertakingRulePlotBands,
  createAxisRulePlotLines,
  hideRulePlotLinesOrBands,
  showRulePlotLinesOrBands,
} from "./rule";
import { FAULT_STATUS_DESCRIPTIONS, QUANTITY_VALUE_VALIDITY } from "./status";
import { drawMarker as defineFailureStatusMarker } from "../../../../../../../Chart/series/getFailureSeries";
import type { FailureValueStatus } from "../../../../../../../Chart/PointMetadata";
import type { EncodingT } from "../../../../../../schema/encoding";
import { capitalize } from "diagnostics-lib/string";
import formatNumber from "@ui/i18n/formatNumber";
import formatDate, { DAY_MS } from "@ui/i18n/formatDate";
import { getTimeFormat } from "@ui/Highcharts/BetterHighcharts";
import { color } from "@ui";
import type { useAvailableResources } from "../../../../../../hook/useAvailableResources";
import { applyInvalidatedDataPointsOption } from "./invalidatedDataPoints";

// #region utils
function formatColumn(column: string) {
  return column !== "validity" && column !== "faultStatus"
    ? `${t("project.dashboard.resource.column", column)}`
    : "";
}

function formatUnit(unit?: string) {
  return unit && unit !== "null" ? ` — ${unit}` : "";
}

function quantityImpactsUnit(
  quantity: string,
  dataset: { [key: string]: boolean | number | string | null | undefined }[],
) {
  const units: (boolean | number | string | null | undefined)[] = [];
  for (const row of dataset) {
    if (!row[`impactUnit/${quantity}`]) continue;
    units.push(row[`impactUnit/${quantity}`]);
  }
  return `${units[0] ?? ""}`;
}

function quantitativeUnit(
  identifier: string,
  dataset: { [key: string]: boolean | number | string | null | undefined }[],
) {
  return `${dataset.find(row => row["identifier"] === identifier)?.["unit"]}`;
}
// #endregion utils

// #region definePoints
function definePoints(
  encoding: EncodingT,
  dataset: { [key: string]: boolean | number | string | null | undefined }[],
  color: EncodingT | undefined,
) {
  const values = [];
  const categorySet = new Set<string>();

  let type;

  for (const row of dataset) {
    if (row["identifier"] !== encoding.identifier) continue;

    const raw =
      encoding.column === "quantityImpacts"
        ? row[`quantityImpacts/${encoding.quantity}`]
        : row[encoding.column];

    values.push({
      kind: row["kind"],
      date: row["date"],
      group: row["group"],
      label: row["label"],
      description: row["description"],
      displayName: row["displayName"],
      validity: row["validity"],
      faultStatus:
        color?.column === "faultStatus" && color.identifier === row["identifier"]
          ? row["faultStatus"]
          : undefined,
      raw:
        encoding.column !== "date" &&
        (row["faultStatus"] === "REJECTED" || row["faultStatus"] === "LEGACY_REJECTED")
          ? 0
          : raw,
    });
    type =
      (typeof raw === "number" || typeof raw === "undefined" || raw === null) &&
      (type === undefined || type === DataType.Quantitative)
        ? DataType.Quantitative
        : DataType.Nominal;

    if (type === DataType.Nominal) categorySet.add(raw?.toString() ?? "");
  }

  type = encoding.column === "date" ? DataType.Temporal : (type ?? DataType.Nominal);

  const unit =
    encoding.column === "quantityImpacts" && encoding.quantity
      ? quantityImpactsUnit(encoding.quantity, dataset)
      : type === DataType.Quantitative &&
          encoding.column !== "normalizedGap" &&
          encoding.column !== "globalImpact"
        ? quantitativeUnit(encoding.identifier, dataset)
        : "";

  return {
    values,
    categories: Array.from(categorySet.values()),
    type,
    unit,
  };
}

function pointValueFormatter(
  value: boolean | string | number | null | undefined,
  type: string,
  unit: string,
) {
  return value !== null && value !== undefined
    ? type === DataType.Temporal && typeof value === "number"
      ? DateTime.fromMillis(value)
          .setLocale(locale)
          .toFormat(`dd LLL yyyy, ${locale === "en" ? "h" : "H"}:mm ${locale === "en" ? "a" : ""}`)
      : type === DataType.Quantitative && typeof value === "number"
        ? `${formatNumber(value, { precision: 2 })}${unit === "null" ? "" : ` ${unit}`}`
        : type === DataType.Nominal
          ? `${value}`
          : undefined
    : undefined;
}

// #endregion definePoints

// #region defineSeries
export type Series = Highcharts.SeriesOptionsType & {
  xAxis: string;
  yAxis: string;
  color: string | undefined;
  data: Highcharts.PointOptionsObject[];
  custom: {
    x: Omit<ReturnType<typeof defineSeriePoints>["x" | "y"], "values"> & {
      identifier: string;
      column: string;
      quantity?: string;
      name: string;
    };
    y: Omit<ReturnType<typeof defineSeriePoints>["x" | "y"], "values"> & {
      identifier: string;
      column: string;
      quantity?: string;
      name: string;
    };
    rule: BlockPlotModelT["config"]["layers"][number]["rule"];
    zoom: BlockPlotModelT["config"]["layers"][number]["zoom"];
    position: BlockPlotModelT["config"]["layers"][number]["position"];
    boundaries: { x: { min: number; max: number }; y: { min: number; max: number } };
    discriminant: {
      column: string | undefined;
      values: (string | number)[];
      serieColor: string | undefined;
      name?: string;
    };
  };
};

type PointInfo = {
  identifier: string;
  column: string;
  quantity: string | undefined;
  unit: string;
  type: string;
};

type Point = Highcharts.PointOptionsObject & {
  custom: {
    date: number;
    group: string;
    label: string;
    color: string | number | undefined;
    x: Omit<ReturnType<typeof definePoints>["values"][number], "date" | "group" | "label"> &
      PointInfo;
    y: Omit<ReturnType<typeof definePoints>["values"][number], "date" | "group" | "label"> &
      PointInfo;
  };
};

function defineSeriePointMarkerColor(
  layerColorColumn: string,
  c: string | number | undefined,
  i: number,
  colors: (typeof c)[],
) {
  switch (layerColorColumn) {
    case "validity":
      return typeof c === "string" ? getValidityColor(c) : undefined;
    case "date":
      return getDefaultSequentialColor(colors.indexOf(i) / colors.length);
    case "hour":
      return getDefaultSequentialColor(colors.indexOf(i) / colors.length);
    case "dayOfYear":
      return getDefaultSequentialColor(colors.indexOf(i) / colors.length);
    default:
      return getDefaultColor(colors.findIndex(x => x === c));
  }
}

// const defineSeriePointMarkerSymbol = (layerColorColumn: string, c: string | number | undefined) => {
//   switch (layerColorColumn) {
//     case "validity":
//       return typeof c === "string" ? getValidityShape(c) : "circle";
//     default:
//       return "circle";
//   }
// };

function definePointValue(
  kind: string,
  type: DataType,
  raw: string | number | boolean | null | undefined,
  categories: (string | number | boolean | null | undefined)[],
) {
  switch (type) {
    case DataType.Nominal:
      return typeof raw === "string" ? categories.indexOf(raw) : null;
    case DataType.Quantitative:
      return typeof raw === "number" ? (kind === "FAULT" && raw === 0 ? null : raw) : null;
    case DataType.Temporal:
      return typeof raw === "number" ? raw : null;
    default:
      return null;
  }
}

export function definePointMarker(
  mark: "line" | "scatter" | string,
  column: string,
  value: string | number,
  defaultColor: string | undefined,
  values?: (boolean | string | number | null | undefined)[],
) {
  switch (column) {
    case "faultStatus":
      return {
        // enabled: true,
        enabledThreshold: 2,
        ...defineFailureStatusMarker(value as FailureValueStatus, defaultColor ?? ""),
      };
    case "validity":
      return {
        enabled: typeof value === "string" ? enableValidityMarker(mark, value) : false,
        enabledThreshold: 2,
        symbol: typeof value === "string" ? getValidityShape(value) : "circle",
        ...(mark === "scatter" && value === "INVALID_MANUAL" ? { radius: 6 } : {}),
        lineColor:
          typeof value === "string" ? (getValidityColor(value) ?? defaultColor) : defaultColor,
        fillColor:
          value === "VALID_PARTIAL_INPUTS"
            ? color.darkNitrogen50
            : typeof value === "string"
              ? (getValidityColor(value) ?? defaultColor)
              : defaultColor,
        lineWidth: value === "VALID_PARTIAL_INPUTS" ? 2 : 1,
      };
    case "date":
      if (values && values.length > 0) {
        return {
          enabled: true,
          symbol: "circle",
          fillColor: getDefaultSequentialColor(values.indexOf(value) / values.length),
        };
      }
      return {
        enabledThreshold: 2,
        symbol: "circle",
        fillColor: defaultColor,
      };
    case "hour":
      if (values && values.length > 0) {
        const sortedValues = [...values].sort((a, b) => Number(a) - Number(b));
        const index = Math.max(0, sortedValues.indexOf(value));
        return {
          enabled: true,
          symbol: "circle",
          fillColor: getDefaultSequentialColor(index / sortedValues.length),
        };
      }
      return {
        enabledThreshold: 2,
        symbol: "circle",
        fillColor: defaultColor,
      };
    case "dayOfYear":
      if (values && values.length > 0) {
        const sortedValues = [...values].sort((a, b) => Number(a) - Number(b));
        const index = Math.max(0, sortedValues.indexOf(value));
        return {
          enabled: true,
          symbol: "circle",
          fillColor: getDefaultSequentialColor(index / sortedValues.length),
        };
      }
      return {
        enabledThreshold: 2,
        symbol: "circle",
        fillColor: defaultColor,
      };
    default:
      return {
        enabledThreshold: 2,
        symbol: "circle",
        fillColor: values ? getDefaultColor(values.findIndex(x => x === value)) : undefined,
      };
  }
}

export function defineSeriePoints(
  layer: BlockPlotModelT["config"]["layers"][number],
  dataset: { [key: string]: boolean | number | string | null | undefined }[],
  serieColor: string | undefined,
  initialTemporalZoom?: { from: number | null; to: number | null },
): {
  x: Omit<ReturnType<typeof definePoints>, "values">;
  y: Omit<ReturnType<typeof definePoints>, "values">;
  colors: (string | number)[];
  points: Point[];
} {
  const x = definePoints(layer.encoding.x, dataset, layer.encoding.color);
  const y = definePoints(layer.encoding.y, dataset, layer.encoding.color);

  const c = dataset
    .filter(row => row["identifier"] === layer.encoding.color?.identifier)
    .map(d => {
      const x = d[layer.encoding.color?.column ?? -1];
      return x === "null" || !x ? "UNCATEGORIZED" : x;
    })
    .filter((v): v is string | number => v !== "REJECTED" && v !== "LEGACY_REJECTED");

  const colors = Array.from(new Set(c));

  return {
    x: omitObject(x, ["values"]),
    y: omitObject(y, ["values"]),
    colors,

    // @ts-ignore
    points: x.values
      .map(
        (
          { date, raw, group, label, description, kind, validity, faultStatus, displayName },
          i,
        ) => ({
          x: definePointValue(String(kind), x.type, raw, x.categories ?? []),
          y: definePointValue(
            String(y.values[i]?.kind),
            y.type,
            y.values[i]?.raw,
            y.categories ?? [],
          ),
          marker: {
            ...definePointMarker(
              layer.mark,
              layer.encoding.color?.column ?? "",
              c[i] ?? "",
              serieColor,
              colors,
            ),
            /** To keep point sync with timeserie */
            ...(layer.mark === "scatter" && (typeof date === "number" || typeof date === "string")
              ? {
                  enabled:
                    (initialTemporalZoom?.from ?? -Infinity) < new Date(date).getTime() &&
                    new Date(date).getTime() < (initialTemporalZoom?.to ?? Infinity),
                }
              : {}),
          },
          // enableMouseTracking: false,
          custom: {
            date: typeof date === "number" ? date : 0,
            group: group ? `${group === "null" ? "" : group}` : "",
            label: label ? `${label}` : "",
            color: c[i],
            x: {
              identifier: layer.encoding.x.identifier,
              column: layer.encoding.x.column,
              quantity: layer.encoding.x.quantity,
              unit: x.unit,
              type: x.type,
              description: description ? `${description}` : "",
              displayName: displayName ?? null,
              kind: kind ? `${kind}` : "",
              raw,
              validity,
              faultStatus,
            },
            y: {
              identifier: layer.encoding.y.identifier,
              column: layer.encoding.y.column,
              quantity: layer.encoding.y.quantity,
              unit: y.unit,
              type: y.type,
              description: y.values[i]?.description ? `${y.values[i]?.description}` : "",
              displayName: y.values[i]?.displayName ?? null,
              kind: y.values[i]?.kind ? `${y.values[i]?.kind}` : "",
              raw: y.values[i]?.raw,
              faultStatus: y.values[i]?.faultStatus,
              validity: y.values[i]?.validity,
            },
          },
        }),
      )
      .sort((a, b) => (a.x ?? 0) - (b.x ?? 0)),
  };
}

function defineSerieBoundaries(points: Highcharts.PointOptionsObject[]) {
  return points.reduce<{
    x: { min: number; max: number };
    y: { min: number; max: number };
  }>(
    (acc, { x, y }) => {
      if (typeof x === "number") {
        acc.x.min = Math.min(acc.x.min || Infinity, x);
        acc.x.max = Math.max(acc.x.max || -Infinity, x);
      }
      if (typeof y === "number") {
        acc.y.min = Math.min(acc.y.min || Infinity, y);
        acc.y.max = Math.max(acc.y.max || -Infinity, y);
      }
      return acc;
    },
    {
      x: {} as { min: number; max: number },
      y: {} as { min: number; max: number },
    },
  );
}

function defineSerieName(
  layer: BlockPlotModelT["config"]["layers"][number],
  xUnit: string,
  yUnit: string,
  availableResources: ReturnType<typeof useAvailableResources>,
  discriminant?: string | number | boolean | null,
  discriminantColumn?: string,
  discriminantIdentifier?: string,
) {
  if (layer.name) return layer.name;

  const iyDisplayNameRaw =
    availableResources.fault.identifiers.find(i => i.name === layer.encoding.y.identifier)
      ?.displayName ?? null;

  const ixDisplayNameRaw =
    availableResources.fault.identifiers.find(i => i.name === layer.encoding.x.identifier)
      ?.displayName ?? null;

  const ixRaw = ixDisplayNameRaw ?? layer.encoding.x.identifier;
  const iyRaw = iyDisplayNameRaw ?? layer.encoding.y.identifier;

  const ix = ixRaw.length < 30 ? ixRaw : ixRaw.slice(0, 30) + "...";
  const iy = iyRaw.length < 30 ? iyRaw : iyRaw.slice(0, 30) + "...";
  const qx = layer.encoding.x.quantity ? ` ${layer.encoding.x.quantity}` : "";
  const qy = layer.encoding.y.quantity ? ` ${layer.encoding.y.quantity}` : "";
  const cx = formatColumn(layer.encoding.x.column);
  const cy = formatColumn(layer.encoding.y.column);
  const ux = formatUnit(xUnit);
  const uy = formatUnit(yUnit);

  if (layer.mark === "scatter") {
    const prefix = layer.encoding.color ? formatColumn(layer.encoding.color.column) : null;

    const suffix =
      discriminantColumn !== "group" && discriminantIdentifier
        ? ` — ${discriminantIdentifier}`
        : "";

    const d =
      discriminantColumn === "validity"
        ? QUANTITY_VALUE_VALIDITY[`${discriminant}`]
        : discriminantColumn === "faultStatus"
          ? FAULT_STATUS_DESCRIPTIONS[`${discriminant}`]
          : typeof discriminant === "string"
            ? discriminant.split("-")?.[0]?.replace("null", "")
            : (discriminant ?? "");

    const formattedValue =
      d !== "UNCATEGORIZED" && d !== "null" && d !== ""
        ? d
        : prefix
          ? t("project.dashboard.block.plot.na_category")
          : capitalize(t("project.dashboard.block.plot.na_category"));

    return layer.encoding.color
      ? `${prefix} ${formattedValue}${suffix}`
      : `${ix} (${cx}${qx})${ux} x ${iy} (${cy}${qy})${uy}`;
  }

  return `${iy} (${cy}${qy})${uy}`;
}

function defineCustomAxisName(
  encoding: EncodingT,
  unit: string,
  { displayName, displayColumn }: { displayName: string | null; displayColumn?: boolean },
) {
  const c = formatColumn(encoding.column);
  const q = encoding.quantity ? ` ${encoding.quantity}` : "";
  const cq = displayColumn ? `(${c}${q})` : "";
  const u = formatUnit(unit);
  let i = displayName ?? encoding.identifier;
  i = i.length < 30 ? i : i.slice(0, 30) + "...";
  return `${i} ${cq}${u}`;
}

function sortByColors(
  colors: (string | number)[],
  a: string | number | undefined,
  b: string | number | undefined,
) {
  if (a && b) {
    const ia = colors.indexOf(a);
    const ib = colors.indexOf(b);
    if (ia < ib) return -1;
    if (ia > ib) return 1;
  }
  return 0;
}

function hideSerie(this: Highcharts.Series) {
  hideRulePlotLinesOrBands(this);
}
function showSerie(this: Highcharts.Series) {
  showRulePlotLinesOrBands(this);
}

function defineSeries(
  blockId: BlockPlotModelT["id"],
  blockConfig: BlockPlotModelT["config"],
  datasets: { [key: string]: boolean | number | string | null | undefined }[],
  {
    groupedByUnit = false,
    replicateInNavigator = false,
    initialTemporalZoom,
  }: {
    groupedByUnit?: boolean;
    replicateInNavigator?: boolean;
    displayCycle?: boolean;
    initialTemporalZoom?: { from: number | null; to: number | null };
  },
  availableResources: ReturnType<typeof useAvailableResources>,
): Series[] {
  return blockConfig.layers.flatMap((layer, layerIndex) => {
    const serieColor = getDefaultColor(layerIndex);
    const { x, y, colors, points } = defineSeriePoints(
      layer,
      datasets,
      serieColor,
      initialTemporalZoom,
    );
    const boundaries = defineSerieBoundaries(points);

    const discriminatedPoints = groupBy(points, p =>
      layer.mark === "scatter"
        ? layer.encoding.color?.column === "date" ||
          layer.encoding.color?.column === "hour" ||
          layer.encoding.color?.column === "dayOfYear"
          ? layer.encoding.color?.column
          : p.custom?.["color"]
        : p.custom?.["group"],
    );

    if (discriminatedPoints.size > 1 && discriminatedPoints.has(undefined))
      // eslint-disable-next-line unicorn/no-useless-undefined
      discriminatedPoints.delete(undefined);

    const markerColors = [...discriminatedPoints.keys()]
      .sort((a, b) => sortByColors(colors, a, b))
      .map(
        (k, i) =>
          defineSeriePointMarkerColor(layer.encoding.color?.column ?? "", k, i, colors) ??
          serieColor,
      );

    return [...discriminatedPoints.entries()]
      .sort((a, b) => sortByColors(colors, a[0], b[0]))
      .map(([discriminant, data], i, arr) => {
        const discriminants = arr.map(([d]) => d?.toString().replaceAll(" ", "_"));
        const id = `${blockId}-${layer.mark}-${layer.encoding.x.identifier}-${layer.encoding.x.column}-${layer.encoding.y.identifier}-${layer.encoding.y.column}-${layer.encoding.y.quantity ? layer.encoding.y.quantity + "-" : ""}${discriminants[i]}-${i}`;
        const linkedTo =
          layer.mark === "line" && i > 0
            ? `${blockId}-${layer.mark}-${layer.encoding.x.identifier}-${layer.encoding.x.column}-${layer.encoding.y.identifier}-${layer.encoding.y.column}-${discriminants[i - 1]}-${i - 1}`
            : undefined;
        const xAxis = `${blockId}-${layer.encoding.x.column}`;
        const yAxis = `${blockId}-${y.unit}${
          groupedByUnit ? "" : `-${layer.encoding.y.column}-${layer.encoding.y.identifier}`
        }`;

        return {
          id,
          index: i,
          color:
            layer.mark === "scatter" &&
            layer.encoding.color?.column !== "date" &&
            layer.encoding.color?.column !== "hour" &&
            layer.encoding.color?.column !== "dayOfYear"
              ? markerColors[i]
              : serieColor,
          linkedTo,
          events: {
            hide: hideSerie,
            show: showSerie,
          },
          name: defineSerieName(
            layer,
            x.unit,
            y.unit,
            availableResources,
            discriminant,
            layer.encoding.color?.column,
            layer.encoding.color?.identifier,
          ),
          states: {
            /** @note disabled for linkedTo series */
            hover: {
              enabled: layer.mark !== "line",
            },
            inactive: {
              enabled: layer.mark !== "line",
            },
          },
          custom: {
            x: {
              identifier: layer.encoding.x.identifier,
              column: layer.encoding.x.column,
              quantity: layer.encoding.x.quantity,
              name: defineCustomAxisName(layer.encoding.x, x.unit, {
                displayName: null,
                displayColumn: layer.mark === "scatter",
              }),
              ...x,
            },
            y: {
              identifier: layer.encoding.y.identifier,
              column: layer.encoding.y.column,
              quantity: layer.encoding.y.quantity,
              name: defineCustomAxisName(layer.encoding.y, y.unit, {
                displayName: null,
                displayColumn: layer.mark === "scatter",
              }),
              ...y,
            },
            rule: layer.rule,
            zoom: layer.zoom,
            position: layer.position,
            boundaries,
            discriminant: {
              name: discriminant?.toString().split("-")[0],
              column: layer.encoding.color?.column,
              values: colors,
              serieColor,
            },
          },
          marker:
            layer.mark === "line"
              ? { enabled: false }
              : discriminant
                ? definePointMarker(
                    layer.mark,
                    layer.encoding.color?.column ?? "",
                    discriminant,
                    serieColor,
                    discriminants,
                  )
                : { enabled: true, symbol: "circle" },
          legendIndex: discriminant ? colors.indexOf(discriminant) : undefined,
          xAxis,
          yAxis,
          data: data.every(p => p.x === undefined && p.y === undefined) ? [] : data,
          type: layer.mark as "zigzag",
          tooltip: {
            headerFormat: "",
            pointFormatter,
            sticky: true,
          },
          findNearestPointBy: "xy",
          replicateInNavigator,
          zIndex: layerIndex,
        };
      });
  });
}

// #endregion defineSeries

// #region defineAxis
const axisLabelFormatter = memoize(
  (type: string) =>
    function (self: Highcharts.AxisLabelsFormatterContextObject) {
      try {
        if (type === "datetime" || (type === DataType.Temporal && typeof self.value === "number")) {
          return formatDate(Number(self.value), getTimeFormat(self.chart), self.chart?.timezone);
        }

        if (type === DataType.Quantitative) {
          return `${self.value}`;
        }

        return self.axis?.defaultLabelFormatter?.call(self) ?? `${self.value}`;
      } catch (error) {
        console.debug("axisLabelFormatter error", error);
        return "invalid";
      }
    },
);

function groupAxis<A extends Highcharts.XAxisOptions | Highcharts.YAxisOptions>(
  axis: A[],
  series: ReturnType<typeof defineSeries>,
  direction: "x" | "y",
  availableResources: ReturnType<typeof useAvailableResources>,
) {
  const breaks = uniqBy(
    axis.flatMap(a => a.breaks).filter(Boolean),
    b => `${b?.from}-${b?.to}-${b?.breakSize}`,
  );

  const isTemporalAxis = axis.every(a => a.type === "datetime");
  const areScatter = series.every(s => s.type === "scatter");

  const seriesByAxis = groupBy(series, direction === "x" ? "xAxis" : "yAxis");
  const mergedAxis = groupBy(axis, "id").mapValues(axis => {
    if (axis.length === 1) return axis[0];

    const plotLines = uniqBy(
      axis.flatMap(a => a.plotLines),
      pl => `${pl?.value}`,
    );

    const plotBands = uniqBy(
      axis.flatMap(a => a.plotBands).filter(Boolean),
      pb => `${pb?.from}-${pb?.to}`,
    );

    const min = Math.min(...axis.map(a => a.min ?? Infinity));
    const max = Math.max(...axis.map(a => a.max ?? -Infinity));
    const initialUserMin = Math.min(...axis.map(a => a.initialUserMin ?? Infinity));
    const initialUserMax = Math.max(...axis.map(a => a.initialUserMax ?? -Infinity));

    if (direction === "x" && isTemporalAxis) {
      return {
        ...axis[0],
        breaks,
        plotBands,
        plotLines,
        title: undefined,
        min: min === Infinity ? undefined : min,
        max: max === -Infinity ? undefined : max,
        initialUserMin: initialUserMin === Infinity ? undefined : initialUserMin,
        initialUserMax: initialUserMax === -Infinity ? undefined : initialUserMax,
      };
    }

    // @ts-ignore
    const series = seriesByAxis.get(axis[0].id);
    const infos = series?.map(s => ({
      ...s.custom[direction],
      position: s.custom["position"],
      color: s.color,
    }));

    const unit =
      infos?.[0]?.unit && infos?.[0]?.unit !== "null"
        ? `<tspan fill=${colors.raw.carbon800}>${infos?.[0]?.unit}</tspan>`
        : undefined;

    const groups = groupBy(
      infos || [],
      info => `${info.column}${info.quantity ? `-${info.quantity}` : ""}`,
    ).mapValues(infos => uniqBy(infos, "identifier"));

    const title = Array.from(groups.entries())
      .map(([_, infos]) => {
        const encodings = infos.map(({ identifier, column, color, quantity }) => {
          const resource = availableResources.fault.identifiers.find(i => i.name === identifier);
          const name = resource?.displayName ?? identifier;
          return `<tspan fill="${areScatter ? colors.raw.carbon800 : color}">${name} ${areScatter ? `(${formatColumn(column)}${quantity ? ` ${quantity}` : ""})` : ""}</tspan>`;
        });

        return `<tspan>${encodings.join(", ")}</tspan>`;
      })
      .join(", ");

    const rotation = direction === "x" ? undefined : getYAxisTitleRotation(infos?.[0]?.position);
    const offset = direction === "x" ? undefined : getYAxisTitleOffset(infos?.[0]?.position);

    return {
      ...axis[0],
      breaks,
      plotBands,
      plotLines: plotLines.map(pl => ({
        ...pl,
        label: {
          ...pl?.label,
          align: axis[0]?.opposite ? "right" : "left",
        },
      })),
      title: {
        text: `${title}${formatUnit(unit)}`,
        style: { color: colors.raw.carbon800 },
        rotation,
        offset,
      },
      min: min === Infinity ? undefined : min,
      max: max === -Infinity ? undefined : max,
      initialUserMin: initialUserMin === Infinity ? undefined : initialUserMin,
      initialUserMax: initialUserMax === -Infinity ? undefined : initialUserMax,
    };
  });

  return Array.from(mergedAxis.values()).filter(
    (axis): axis is NonNullable<typeof axis> => typeof axis !== "undefined",
  ) as A[];
}

/**
 * @description
 * Infer xAxis from serie definitions.
 */
function defineSeriesXAxis(
  series: ReturnType<typeof defineSeries>,
  displayCycle = false,
  showOvertakingPlotBands = false,
  availableResources: ReturnType<typeof useAvailableResources>,
) {
  const breaks = series
    .map(({ name, data, custom }, i, arr) => {
      if (i === 0) {
        return {
          from: data[0].custom.date,
          to: data[0].custom.date,
          breakSize: 0,
          breakName: custom.discriminant.name ?? "",
        };
      }

      const previousSerie = arr[i - 1];
      if (!previousSerie || previousSerie.name !== name) return null;

      const prev = previousSerie?.data.at(-1).custom.date;
      const next = data[0].custom.date;
      return {
        from: prev,
        to: next,
        breakSize: displayCycle ? next - prev : Math.min(DAY_MS, (next - prev) / 10),
        breakName: custom.discriminant.name ?? "",
      };
    })
    .filter(
      (b): b is NonNullable<{ from: number; to: number; breakSize: number; breakName: string }> =>
        b !== null,
    )
    .map(({ from, to, breakSize, breakName }) => ({
      from,
      to,
      color: "var(--interGroupArea)",
      breakSize,
      breakName: breakName === "null" ? "" : breakName,
    }));

  const breakLines = breaks.map(({ to, breakName }) => ({
    value: to,
    width: 2,
    color: colors.raw.titanium100,
    label: {
      useHTML: true,
      align: "left" as AlignValue,
      text: breakName,
      rotation: 0,
      style: {
        color: colors.raw.carbon600,
        padding: `${spacings.raw.xs}px ${spacings.raw.m}px`,
        fontWeight: "normal",
        fontSize: "10px",
        pointerEvents: "none",
      },
    },
  }));

  const axes: Highcharts.XAxisOptions[] = series.map(({ id, data, xAxis, color, custom, type }) => {
    const displayName =
      availableResources.fault.identifiers.find(i => i.name === custom.x.identifier)?.displayName ??
      null;

    const titleText =
      custom.x.type === DataType.Temporal
        ? null
        : defineCustomAxisName(
            {
              identifier: displayName ?? custom.x.identifier,
              column: custom.x.column,
              quantity: custom.x.quantity,
            },
            custom.x.unit,
            { displayColumn: type === "scatter", displayName },
          );

    const overtakingPlotBands =
      showOvertakingPlotBands && type === "line"
        ? createAxisOvertakingRulePlotBands(id, data, custom.rule?.y)
        : [];

    return {
      id: xAxis,
      categories:
        custom["x"].type === DataType.Nominal
          ? custom["x"].categories?.map(x => `${x}`)
          : undefined,
      crosshair: true,
      type: (custom["x"].type === DataType.Temporal
        ? "datetime"
        : custom["x"].type === DataType.Quantitative
          ? "linear"
          : "category") as Highcharts.AxisTypeValue,
      breaks,
      plotLines: [
        ...breakLines,
        ...createAxisRulePlotLines(
          id,
          custom["x"].type,
          custom.rule?.x ?? [],
          custom["x"].unit,
          color,
          "x",
          "left",
        ),
      ],
      plotBands: [...breaks, ...overtakingPlotBands],
      scrollbar: {
        enabled: false,
      },
      tickPixelInterval: 150,
      labels: {
        formatter: axisLabelFormatter(custom["x"].type),
      },
      min: custom["boundaries"].x.min,
      max: custom["boundaries"].x.max,
      minRange: 0.01,
      minPadding: 0.1,
      title: {
        text: titleText,
        style: { color: type === "scatter" ? colors.raw.carbon800 : color },
      },
      initialUserMin: convertToAxisValue(custom["x"].type, custom["zoom"]?.x?.min),
      initialUserMax: convertToAxisValue(custom["x"].type, custom["zoom"]?.x?.max),
    };
  });

  return axes.length > 1
    ? groupAxis<Highcharts.XAxisOptions>(axes, series, "x", availableResources)
    : axes;
}

function getYAxisTitleRotation(position?: string) {
  return position === "right" ? 270 : position === "left" ? -90 : 0;
}

function getYAxisTitleOffset(position?: string) {
  return position === "right" ? 55 : position === "left" ? 50 : 0;
}
/**
 * @description
 * Infer yAxis from serie definitions.
 */
function defineSeriesYAxis(
  series: ReturnType<typeof defineSeries>,
  groupedByUnit = false,
  availableResources: ReturnType<typeof useAvailableResources>,
) {
  const axes = series.map(({ id, yAxis, custom, color, type }, arrIndex) => {
    const opposite =
      custom["position"] === "right"
        ? true
        : custom["position"] === "left"
          ? false
          : arrIndex % 2 === 0;

    const titleRotation = getYAxisTitleRotation(custom["position"]);
    const titleOffset = getYAxisTitleOffset(custom["position"]);

    const displayName =
      availableResources.fault.identifiers.find(i => i.name === custom.y.identifier)?.displayName ??
      null;

    const titleText = defineCustomAxisName(
      {
        identifier: custom.y.identifier,
        column: custom.y.column,
        quantity: custom.y.quantity,
      },
      custom.y.unit,
      { displayColumn: type === "scatter", displayName },
    );

    return {
      crosshair: true,
      type: (custom["y"].type === DataType.Temporal
        ? "datetime"
        : custom["y"].type === DataType.Quantitative
          ? "linear"
          : "category") as Highcharts.AxisTypeValue,
      id: yAxis,
      plotLines: createAxisRulePlotLines(
        id,
        custom["y"].type,
        custom.rule?.y ?? [],
        custom["y"].unit,
        color,
        "y",
        opposite ? "right" : "left",
      ),
      gridLineWidth: arrIndex === 0 ? 1 : 0,
      scrollbar: {
        enabled: false,
      },
      labels: {
        formatter: axisLabelFormatter(custom["y"].type),
      },
      title: {
        text: titleText,
        style: { color: type === "scatter" ? colors.raw.carbon800 : color },
        rotation: titleRotation,
        offset: titleOffset,
      },
      min: custom["boundaries"].y.min,
      max: custom["boundaries"].y.max,
      opposite,
      initialUserMin: convertToAxisValue(custom["y"].type, custom["zoom"]?.y?.min),
      initialUserMax: convertToAxisValue(custom["y"].type, custom["zoom"]?.y?.max),
    };
  });

  return uniqBy(
    groupedByUnit
      ? groupAxis<Highcharts.YAxisOptions>(axes, series, "y", availableResources)
      : axes.map(a => ({
          ...a,
          crosshair:
            a.crosshair === undefined
              ? false
              : typeof a.crosshair === "boolean"
                ? a.crosshair
                : true,
        })),
    "id",
  );
}

// #endregion defineAxis

// #region defineOptions
function legendItemClick(this: Highcharts.Series, e: Highcharts.SeriesLegendItemClickEventObject) {
  if (e.browserEvent.shiftKey) {
    e.preventDefault();
    for (const serie of this.chart.series) {
      serie.setVisible(true, false);
    }
  }

  if (e.browserEvent.altKey) {
    e.preventDefault();
    for (const serie of this.chart.series) {
      if (serie !== e.target) {
        serie.setVisible(!serie.visible, false);
      }
    }
  }
}

function chartRedraw(this: Highcharts.Chart) {
  for (const s of this.series.filter(s => s.visible)) {
    showRulePlotLinesOrBands(s);
  }
  for (const s of this.series.filter(s => !s.visible)) {
    hideRulePlotLinesOrBands(s);
  }
}

export function defineOptions(
  id: BlockPlotModelT["id"],
  config: BlockPlotModelT["config"],
  datasets: { [key: string]: boolean | number | string | null | undefined }[],
  availableResources: ReturnType<typeof useAvailableResources>,
  initialTemporalZoom?: { from: number | null; to: number | null },
): Options | null {
  const groupedByUnit =
    config.layers.every(({ mark }) => mark === "scatter") ||
    (config.options?.groupYAxesByUnit ?? false);

  const navigatorEnabled =
    config.options?.navigator &&
    config.layers.reduce((acc, l) => acc && l.encoding.x.column === "date", true);

  function hasValidXY(
    p: Highcharts.PointOptionsObject | null | undefined,
  ): p is Highcharts.PointOptionsObject & { x: number; y: number } {
    return (
      p !== null &&
      !Array.isArray(p) &&
      typeof p === "object" &&
      "x" in p &&
      "y" in p &&
      p.x !== null &&
      p.y !== null
    );
  }

  const baseSeries = defineSeries(
    id,
    config,
    datasets,
    {
      groupedByUnit: config.options?.groupYAxesByUnit ?? false,
      displayCycle: config.options?.displayCycle ?? false,
      replicateInNavigator: navigatorEnabled,
      initialTemporalZoom,
    },
    availableResources,
  ).filter(s => s.data.some(hasValidXY));

  const xAxis = defineSeriesXAxis(
    baseSeries,
    config.options?.displayCycle,
    config.options?.showOvertakingPlotBands,
    availableResources,
  );

  const yAxis = defineSeriesYAxis(baseSeries, groupedByUnit, availableResources);

  const series = applyInvalidatedDataPointsOption(
    baseSeries,
    config.options?.displayInvalidatedDataPoints ?? false,
  ).filter(s => s.data.some(hasValidXY));

  if (xAxis.length === 0 || yAxis.length === 0) {
    return null;
  }

  return {
    initial: {
      xAxis: xAxis.map(x => ({
        initialUserMin:
          x.type === "datetime"
            ? (initialTemporalZoom?.from ?? x.initialUserMin)
            : x.initialUserMin,
        initialUserMax:
          x.type === "datetime" ? (initialTemporalZoom?.to ?? x.initialUserMax) : x.initialUserMax,
      })),
    },
    chart: {
      alignTicks: false,
      className: id,
      /** @note Prevent latest top tick to be cut by the svg frame. */
      marginTop: 16,

      /** @note Margin reset to automatically correct display of axis with opposite: true. */
      marginRight: undefined,
      marginLeft: undefined,
      spacingRight: undefined,
      zoomType: "xy",
      panKey: "alt",
      panning: {
        enabled: true,
        type: "xy",
      },
      animation: false,
      events: {
        redraw: chartRedraw,
      },
    },
    legend: config.layers.every(l =>
      l.encoding.color?.column
        ? ["date", "hour", "dayOfYear"].includes(l.encoding.color.column)
        : false,
    )
      ? { enabled: false }
      : {
          align: "right",
          enabled: true,
          itemStyle: {
            color: colors.raw.carbon800,
            fontWeight: "semibold",
          },
        },
    plotOptions: {
      series: {
        events: {
          legendItemClick,
        },
      },
    },
    xAxis,
    yAxis,
    series,
    navigator: {
      enabled: navigatorEnabled,
      xAxis: {
        breaks: xAxis.flatMap(x => x.breaks) as NavigatorXAxisBreaksOptions[],
        plotBands: xAxis.flatMap(x => x.plotBands) as NavigatorXAxisPlotBandsOptions[],
        max: Date.now(),
      },
    },
  };
}
// #endregion defineOptions

// #region pointFormatter
const dashboardTooltip = css`
  width: 100%;
  min-width: 250px;
  max-width: 350px;
  z-index: 9999;
  position: relative;
`;
const analysisInfo = css`
  color: ${colors.carbon800};
`;

const pointFormatterIdentifier = css`
  &,
  & p {
    width: 100%;
    overflow: hidden;
    overflow-wrap: normal;
    text-overflow: ellipsis;
  }

  &:not(:first-child) {
    margin-top: ${spacings.xs};
  }
`;

const pointFormatterValue = css`
  white-space: nowrap;
`;

const pointFormatterSeparator = css`
  margin: ${spacings.s} 0px;
  height: 0px;
  border: none;
  border-top: 1px solid ${colors.titanium200};
`;

const pointFormatterDescription = css`
  color: ${colors.carbon700};
  margin-bottom: ${spacings.xs};
  white-space: initial;
`;

export function pointFormatter(this: Highcharts.Point) {
  const custom = this.custom as unknown as ReturnType<
    typeof defineSeriePoints
  >["points"][number]["custom"];

  const entries = Array.from(
    groupBy([custom.x, custom.y], i => [i["identifier"]])
      .mapValues(values => values.filter(({ column }) => column !== "date"))
      .entries(),
  );

  const validities = groupBy([custom.x, custom.y], "identifier").mapValues(
    values =>
      uniqBy(
        values.map(v => pickObject(v, ["validity"])),
        "validity",
      )[0],
  );

  const faultStatuses = groupBy([custom.x, custom.y], "identifier").mapValues(
    values =>
      uniqBy(
        values.map(v => pickObject(v, ["faultStatus"])),
        "faultStatus",
      )[0],
  );

  const formattedDate = DateTime.fromMillis(custom.date)
    .setLocale(locale)
    .toFormat(`dd LLL yyyy, ${locale === "en" ? "h" : "H"}:mm ${locale === "en" ? "a" : ""}`);

  return renderToString(
    <div className={dashboardTooltip}>
      {/* {(custom.group.length > 0 || custom.label.length > 0) && ( */}
      <>
        <Text type="body2" className={analysisInfo}>
          {custom.group &&
            custom.group.length > 0 &&
            custom.group.split("-")[0] !== "null" &&
            t("commons.analysis_title.group", { group: custom.group.split("-")[0] ?? "" })}
          {custom.label && custom.label.length > 0 && (
            <span>
              {" "}
              {custom.group.length > 0 && custom.group.split("-")[0] !== "null" ? "—" : ""}{" "}
              {t("commons.analysis_title.label", { label: custom.label })}
            </span>
          )}
          {(custom.group.length > 0 || custom.label.length > 0) && <br />}
          <span>{formattedDate}</span>
        </Text>
        <hr className={pointFormatterSeparator} />
      </>
      {/* )} */}
      {entries.map(([identifier, values], i, arr) => (
        <React.Fragment key={identifier}>
          <div className={pointFormatterIdentifier}>
            <Text type="body1SemiBold" as="p">
              {values[0]?.displayName ? values[0]?.displayName : identifier}
            </Text>
            <Text type="body3" className={pointFormatterDescription}>
              {t(
                "project.dashboard.encoding.kind",
                typeof values[0]?.kind === "string" ? values[0]?.kind : "",
              )}{" "}
              {typeof values[0]?.description === "string" && values[0]?.description.length > 0
                ? "—"
                : ""}{" "}
              {values[0]?.description && values[0]?.description}
            </Text>
            <div>
              {values.map(v => (
                <Fragment key={v.column}>
                  <Text type="body2" className={pointFormatterValue}>
                    {t("project.dashboard.resource.column", v.column)}
                    {v.column === "quantityImpacts" ? " " + v.quantity : ""}
                    {locale === "fr" ? " " : ""}:&nbsp;
                    <Text type="body2SemiBold" as="span">
                      {pointValueFormatter(v.raw, v.type, v.unit)}
                    </Text>
                  </Text>
                </Fragment>
              ))}
              {validities.get(identifier)?.validity && (
                <>
                  <Text type="body2" className={pointFormatterValue}>
                    {t("project.dashboard.plot.tooltip.validity")}&nbsp;
                    <Text type="body2SemiBold" as="span">
                      {QUANTITY_VALUE_VALIDITY[String(validities.get(identifier)?.validity ?? "")]}
                    </Text>
                  </Text>
                </>
              )}
              {faultStatuses.get(identifier)?.faultStatus && (
                <>
                  <Text type="body2" className={pointFormatterValue}>
                    {t("project.dashboard.plot.tooltip.faultStatus")}&nbsp;
                    <Text type="body2SemiBold" as="span">
                      {
                        FAULT_STATUS_DESCRIPTIONS[
                          String(faultStatuses.get(identifier)?.faultStatus ?? "")
                        ]
                      }
                    </Text>
                  </Text>
                </>
              )}
            </div>
          </div>
          {i < arr.length - 1 && <hr className={pointFormatterSeparator} />}
        </React.Fragment>
      ))}
    </div>,
  );
}
// #endregion pointFormatter
